import { useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import { Search, X, SlidersHorizontal } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { AlertsPanel } from "@/components/AlertsPanel";
import { JobCard } from "@/components/JobCard";
import api from "@/lib/api";

const LABEL_CLS = { mens: "border-volt text-volt", womens: "border-blaze text-blaze", coed: "border-neutral-400 text-neutral-300", unspecified: "border-neutral-600 text-neutral-500" };
const PAGE = 20;
const KEYS = ["q", "program", "level", "state", "role", "sort", "fresh", "page"];

export function ProgramBadge({ program, label }) {
  const p = program || "unspecified";
  return <span data-testid={`program-badge-${p}`} className={`inline-block w-fit self-start shrink-0 text-[10px] tracking-widest border px-1.5 py-0.5 ${LABEL_CLS[p] || LABEL_CLS.unspecified}`}>{(label || p).toUpperCase()}</span>;
}

/* Public job search over ALL safe openings. Filters/sort/page live in the URL (shareable, survive detail → back). */
export default function WeeklyJobs() {
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const f = Object.fromEntries(KEYS.map((k) => [k, params.get(k) || ""]));
  const page = Math.max(1, parseInt(f.page || "1", 10) || 1);
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | error
  const [qInput, setQInput] = useState(f.q);
  const reqId = useRef(0);

  const set = (patch, resetPage = true) => {
    const next = { ...f, ...patch };
    if (resetPage) next.page = "";
    setParams(Object.fromEntries(Object.entries(next).filter(([, v]) => v)));
  };
  const reset = () => { setQInput(""); setParams({}); };
  const active = KEYS.filter((k) => !["sort", "fresh", "page"].includes(k) && f[k]).length + (f.fresh === "all" ? 1 : 0);

  useEffect(() => { setQInput(f.q); }, [f.q]);
  useEffect(() => {
    const id = ++reqId.current;
    setState("loading");
    const qs = new URLSearchParams({ q: f.q, program: f.program, level: f.level, state: f.state, role: f.role, sort: f.sort || "seen", fresh: f.fresh || "recent", page: "1", page_size: String(Math.min(200, PAGE * page)) });
    for (const [k, v] of [...qs.entries()]) if (!v) qs.delete(k);
    api.get(`/public/jobs/search?${qs}`).then(({ data }) => { if (id === reqId.current) { setData(data); setState("ok"); } })
      .catch(() => { if (id === reqId.current) setState("error"); });
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitQ = (e) => { e.preventDefault(); set({ q: qInput.trim() }); };
  const jobs = data?.jobs || [];
  const pc = data?.program_counts || {};
  const facets = data?.facets || { level: [], state: [], role: [] };

  return (
    <div className="min-h-screen bg-appbg text-white flex flex-col">
      <Header />
      <div className="px-4 md:px-8 lg:px-12 py-10 max-w-5xl w-full mx-auto flex-1" data-testid="weekly-jobs-page">
        <div className="text-xs tracking-widest text-blaze">LACROSSE COACHING OPENINGS · FREE · NO ACCOUNT NEEDED</div>
        <h1 className="heading text-4xl sm:text-5xl mt-2">FIND YOUR NEXT LACROSSE COACHING JOB.</h1>

        <form onSubmit={submitQ} className="mt-6 flex gap-2" role="search">
          <label className="flex-1 flex items-center gap-2 bg-appcard border border-neutral-700 px-3">
            <Search size={16} className="text-neutral-500 shrink-0" /><span className="sr-only">Search title, school or location</span>
            <input value={qInput} onChange={(e) => setQInput(e.target.value)} placeholder="Title, school or location…" data-testid="search-input" className="flex-1 bg-transparent py-3 text-sm outline-none min-w-0" />
            {qInput && <button type="button" aria-label="Clear search" onClick={() => { setQInput(""); set({ q: "" }); }} data-testid="search-clear" className="text-neutral-500 hover:text-white"><X size={14} /></button>}
          </label>
          <button type="submit" data-testid="search-submit" className="bg-blaze text-white heading text-sm px-4 hover:glow-blaze">SEARCH</button>
        </form>

        <div className="mt-4 flex flex-wrap gap-2" data-testid="program-filter" role="group" aria-label="Team category">
          {[["", "All teams"], ["mens", "Men's"], ["womens", "Women's"], ["coed", "Coed"], ["unspecified", "Unspecified"]].map(([v, l]) => (
            <button key={v} data-testid={`filter-${v || "all"}`} onClick={() => set({ program: v })}
              className={`px-3 py-1.5 text-xs tracking-widest border ${f.program === v ? "bg-white text-black border-white" : "border-neutral-700 text-neutral-300"}`}>{l.toUpperCase()}{data && v ? ` (${pc[v] ?? 0})` : ""}</button>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-neutral-500">Team category is about the program (Men's / Women's team), not about you.</p>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="filter-controls">
          <Sel id="level" label="Level" value={f.level} onChange={(v) => set({ level: v })} options={facets.level} any="Any level" />
          <Sel id="state" label="State" value={f.state} onChange={(v) => set({ state: v })} options={facets.state} any="Any state" />
          <Sel id="role" label="Role" value={f.role} onChange={(v) => set({ role: v })} options={facets.role} any="Any role" />
          <Sel id="sort" label="Sort" value={f.sort || "seen"} onChange={(v) => set({ sort: v === "seen" ? "" : v })} options={[{ value: "seen", label: "Recently seen open" }, { value: "posted", label: "Newest posted (unknown dates last)" }]} />
          <Sel id="fresh" label="Freshness" value={f.fresh || "recent"} onChange={(v) => set({ fresh: v === "recent" ? "" : v })} options={[{ value: "recent", label: `Seen open in last ${data?.fresh_days || 45} days` }, { value: "all", label: "Include older / unverified" }]} />
        </div>
        {f.fresh === "all" && (
          <div className="mt-3 border border-amber-500/40 bg-amber-500/5 text-amber-300 text-xs p-3" data-testid="stale-warning">
            Older and unverified listings are included. “Last seen open” is the last date we saw the posting live — it may since have been filled. “Added” is when we found it, not when it was posted. We do not re-verify these.
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3 text-sm" data-testid="result-summary">
          {state === "loading" && !data && <span className="text-neutral-400" data-testid="results-loading">Searching openings…</span>}
          {data && <span data-testid="result-count"><strong>{data.total}</strong> {data.total === 1 ? "opening" : "openings"}{active ? " match your filters" : " listed"}{state === "loading" ? " · updating…" : ""}</span>}
          {data && f.fresh !== "all" && data.hidden_by_freshness > 0 && (
            <button onClick={() => set({ fresh: "all" })} data-testid="show-older" className="text-xs text-neutral-400 underline underline-offset-4 hover:text-white">+ {data.hidden_by_freshness} older / unverified hidden — include</button>
          )}
          {active > 0 && <button onClick={reset} data-testid="reset-filters" className="ml-auto inline-flex items-center gap-1 text-xs tracking-widest text-neutral-300 border border-neutral-700 px-3 py-1.5 hover:border-white"><SlidersHorizontal size={12} /> RESET {active} FILTER{active > 1 ? "S" : ""}</button>}
        </div>

        {state === "error" && (
          <div className="mt-6 border border-blaze/40 p-6 text-sm" data-testid="results-error">Couldn't load openings right now. <button onClick={() => set({}, false)} className="underline underline-offset-4 text-blaze">Try again</button></div>
        )}
        <ul className={`mt-4 divide-y divide-neutral-800 border border-neutral-800 ${state === "loading" ? "opacity-60" : ""}`} data-testid="roundup-list" aria-busy={state === "loading"}>
          {jobs.map((j) => <JobCard key={j.id} job={j} search={location.search} />)}
          {state === "ok" && jobs.length === 0 && (
            <li className="p-8 text-sm text-neutral-400" data-testid="results-empty">
              No openings match. {f.fresh !== "all" && data?.hidden_by_freshness > 0 ? <button onClick={() => set({ fresh: "all" })} className="underline underline-offset-4 text-white">Include {data.hidden_by_freshness} older / unverified listings</button> : null}
              {active > 0 && <> · <button onClick={reset} className="underline underline-offset-4 text-white">Reset filters</button></>}
            </li>
          )}
        </ul>
        {data?.has_more && (
          <div className="mt-4 flex items-center justify-between text-xs text-neutral-400">
            <span data-testid="shown-count">Showing {jobs.length} of {data.total}</span>
            <button onClick={() => set({ page: String(page + 1) }, false)} disabled={state === "loading"} data-testid="load-more" className="border border-neutral-600 heading text-sm px-5 py-2.5 hover:border-volt hover:text-volt disabled:opacity-50">LOAD {Math.min(PAGE, data.total - jobs.length)} MORE</button>
          </div>
        )}
        {data && !data.has_more && jobs.length > 0 && <div className="mt-4 text-xs text-neutral-500" data-testid="shown-count">Showing all {data.total}</div>}

        <AlertsPanel />
      </div>
      <Footer />
    </div>
  );
}

function Sel({ id, label, value, onChange, options, any }) {
  return (
    <label className="text-xs text-neutral-400 flex flex-col gap-1">
      <span>{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={`filter-${id}`} className="bg-appcard border border-neutral-700 px-2 py-2 text-sm text-white">
        {any && <option value="">{any}</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}{o.count != null ? ` (${o.count})` : ""}</option>)}
        {value && any && !options.some((o) => o.value === value) && <option value={value}>{value} (0)</option>}
      </select>
    </label>
  );
}
